import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { CloudUpload } from "lucide-react";

import Message from "../../../i18n/Message";
import label from "../../../i18n/label";
import NewWorkspaceFullpageLayout from "../../workspace/layout/NewWorkspaceFullpageLayout";
import { linkToBuilder, linkToTemplateDetail } from "../../../routes";
import useBuilderWebsocket from "../hooks/useBuilderWebsocket";
import {
  loadBuilderChat,
  clearBuilderChat,
  loadSourcesTree,
  setCurrentProject,
  receiveProjectEvent,
  loadCurrentProject,
  updateCurrentProject,
  exportAsTemplate,
  handleAgentFileChange,
} from "../store/builderActionCreators";
import backend from "../../../backend/backend";
import Edit2Icon from "../../../assets/icons/Edit2Icon";
import CheckIcon from "../../../assets/icons/CheckIcon";
import { builderSelectors } from "../store/builderReducer";
import BuilderActions from "../store/builderActions";
import BuilderMessage from "./BuilderMessage";
import BuilderInputArea from "./BuilderInputArea";
import BuilderReplyLoadingState from "./BuilderReplyLoadingState";
import AgentExecutionPanel from "./agent/AgentExecutionPanel";
import ConnectionStatus from "./agent/ConnectionStatus";
import PreviewPanel from "./preview/PreviewPanel";
import Spinner from "../../../assets/icons/Spinner";

import "./ProjectChatPage.scss";

const MOBILE_VIEWS = {
  CHAT: "chat",
  PREVIEW: "preview",
};

const getPlanApprovalState = (message) => {
  if (!message?.metadataJson) return null;
  try {
    return JSON.parse(message.metadataJson)?.planApproval?.state || null;
  } catch (err) {
    return null;
  }
};

const updatePlanMetadata = (message, state) => {
  if (!message) return message;
  let metadata = {};
  if (message.metadataJson) {
    try {
      metadata = JSON.parse(message.metadataJson);
    } catch (err) {
      metadata = {};
    }
  }
  metadata.planApproval = {
    ...(metadata.planApproval || {}),
    state,
    updatedAt: new Date().toISOString(),
  };
  return {
    ...message,
    planApprovalNeeded: state === "pending",
    metadataJson: JSON.stringify(metadata),
  };
};

const ProjectChatPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const nav = useNavigate();
  const { workspaceId, chatId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileView, setMobileView] = useState(MOBILE_VIEWS.CHAT);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);
  const [nameFormElem, setNameFormElem] = useState(null);
  const [nameInputElem, setNameInputElem] = useState(null);
  const messagesEndRef = useRef(null);
  const initialPromptSentRef = useRef(false);
  const optimisticPlanRef = useRef(null);

  // WebSocket
  const {
    socket: ws,
    isReady: wsReady,
    connectionStatus,
    reconnectAttempts,
    maxReconnectAttempts,
    retry: retryConnection,
  } = useBuilderWebsocket();

  // Redux state
  const chat = useSelector(builderSelectors.getChat);
  const messages = useSelector(builderSelectors.getMessages);
  const replyInProgress = useSelector(builderSelectors.getReplyInProgress);
  const replyError = useSelector(builderSelectors.getReplyError);
  const approvingPlan = useSelector(builderSelectors.getApprovingPlan);
  const planningProgress = useSelector(builderSelectors.getPlanningProgress);
  const project = useSelector(builderSelectors.getCurrentProject);
  // Agent state
  const agentSession = useSelector(builderSelectors.getAgentSession);
  const isAgentRunning = useSelector(builderSelectors.isAgentRunning);

  // Template export state
  const templateExporting = useSelector(builderSelectors.isTemplateExporting);

  // Connection resilience state
  const lastEventId = useSelector(builderSelectors.getLastEventId);
  const shouldResumeAgent = useSelector(builderSelectors.shouldResumeAgent);

  const pendingPlanIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.planApprovalNeeded === true) {
        return i;
      }
    }
    return -1;
  }, [messages]);

  const hasPendingPlan = pendingPlanIndex >= 0;
  const hasApprovedPlan = useMemo(() => {
    return messages.some((message) => {
      if (!message?.planMd) return false;
      const approvalState = getPlanApprovalState(message);
      if (approvalState) {
        return approvalState === "approved";
      }
      return message.planApprovalNeeded === false;
    });
  }, [messages]);

  const rollbackOptimisticPlan = useCallback(() => {
    const pending = optimisticPlanRef.current;
    if (!pending?.previousMessage) return;
    dispatch({
      type: BuilderActions.UPDATE_CHAT_MESSAGE,
      message: pending.previousMessage,
    });
    optimisticPlanRef.current = null;
  }, [dispatch]);

  const clearOptimisticPlan = useCallback(() => {
    if (optimisticPlanRef.current) {
      optimisticPlanRef.current = null;
    }
  }, []);

  // Load chat on mount
  useEffect(() => {
    if (workspaceId && chatId) {
      dispatch(loadBuilderChat(intl, workspaceId, chatId));
    }

    return () => {
      dispatch(clearBuilderChat());
    };
  }, [dispatch, intl, workspaceId, chatId]);

  // Load project data when chat loads
  useEffect(() => {
    if (chat?.projectUid && workspaceId) {
      dispatch(loadCurrentProject(workspaceId, chat.projectUid));
    }
  }, [dispatch, chat?.projectUid, workspaceId]);

  // Set current project UID in Redux for per-project state isolation (sources, events)
  useEffect(() => {
    if (chat?.projectUid) {
      dispatch(setCurrentProject(chat.projectUid));
    }
  }, [dispatch, chat?.projectUid]);

  // Refresh project data and sources when agent completes (to get updated port info)
  useEffect(() => {
    if (agentSession?.status === "COMPLETED" && chat?.projectUid && workspaceId) {
      console.log("[ProjectChatPage] Agent completed - refreshing project data and sources");
      dispatch(loadCurrentProject(workspaceId, chat.projectUid));
      dispatch(loadSourcesTree(workspaceId, chat.projectUid));
    }
  }, [agentSession?.status, chat?.projectUid, workspaceId, dispatch]);

  // Subscribe to project events when WebSocket is ready and project is loaded
  useEffect(() => {
    if (ws && wsReady && project?.uid && workspaceId) {
      console.log("[ProjectChatPage] Subscribing to project events for:", project.uid);
      ws.send(JSON.stringify({
        type: "SUBSCRIBE_PROJECT",
        workspaceUid: workspaceId,
        projectUid: project.uid,
      }));
    }
  }, [ws, wsReady, project?.uid, workspaceId]);

  // Send message function
  const sendChatMessage = useCallback((content) => {
    if (!ws || !wsReady || !content.trim()) return;

    dispatch({
      type: BuilderActions.POSTED_MESSAGE,
      content: content.trim(),
    });

    ws.send(JSON.stringify({
      type: "POST_CHAT_MESSAGE",
      workspaceUid: workspaceId,
      chatUid: chatId,
      content: content.trim(),
    }));
  }, [ws, wsReady, dispatch, workspaceId, chatId]);

  // Handle initial prompt from URL
  useEffect(() => {
    const prompt = searchParams.get("prompt");
    if (prompt && wsReady && chat?.uid === chatId && !initialPromptSentRef.current) {
      const decodedPrompt = decodeURIComponent(prompt);
      sendChatMessage(decodedPrompt);
      setSearchParams({}, { replace: true });
      initialPromptSentRef.current = true;
    }
  }, [searchParams, wsReady, chat, chatId, sendChatMessage, setSearchParams]);

  // Set up WebSocket message handlers
  useEffect(() => {
    if (!ws || !wsReady) return;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle error responses
        if (data.error) {
          rollbackOptimisticPlan();
          dispatch({
            type: BuilderActions.MESSAGE_ERROR,
            error: data,
          });
          return;
        }

        // Handle different message types
        // Helper to parse agent event payload (content field is JSON string)
        const parseAgentPayload = (wsData) => {
          if (!wsData.content) return {};
          try {
            return JSON.parse(wsData.content);
          } catch (e) {
            return {};
          }
        };

        // Track event ID for resume (persist to Redux)
        if (data.eventId) {
          dispatch({
            type: BuilderActions.LAST_EVENT_ID_UPDATED,
            eventId: data.eventId,
          });
        }

        switch (data.type) {
          case "POST_CHAT_MESSAGE_RESPONSE":
            dispatch({
              type: BuilderActions.GOT_MESSAGE_RESPONSE,
              message: data.message,
            });
            if (
              optimisticPlanRef.current?.kind === "refine"
              && data.message?.planMd
              && data.message?.planApprovalNeeded === true
            ) {
              clearOptimisticPlan();
            }
            break;

          case "POST_CHAT_MESSAGE_REPLY_CHUNK":
            // Handle streaming chunks if needed in future
            break;

          case "POST_CHAT_MESSAGE_REPLY_FINISH":
            // Handle stream finish if needed
            break;

          // Agent events
          case "AGENT_INIT":
            dispatch({
              type: BuilderActions.AGENT_SESSION_INIT,
              sessionId: data.agentSessionUid,
              status: "RUNNING",
            });
            break;

          case "AGENT_TODO_UPDATE": {
            const payload = parseAgentPayload(data);
            dispatch({
              type: BuilderActions.AGENT_TODO_UPDATE,
              todos: payload.todos || [],
              currentTask: payload.current,
            });
            if (optimisticPlanRef.current?.kind === "approve") {
              clearOptimisticPlan();
            }
            break;
          }

          case "AGENT_FILE_CHANGE": {
            const payload = parseAgentPayload(data);
            const fileChange = {
              path: payload.path,
              action: payload.action,
              timestamp: new Date().toISOString(),
            };
            // Update agent execution panel file changes list
            dispatch({
              type: BuilderActions.AGENT_FILE_CHANGE,
              change: fileChange,
            });
            // Trigger file content reload for open tabs (debounced)
            if (chat?.projectUid) {
              dispatch(handleAgentFileChange(workspaceId, chat.projectUid, fileChange));
            }
            if (optimisticPlanRef.current?.kind === "approve") {
              clearOptimisticPlan();
            }
            break;
          }

          case "AGENT_TOOL_CALL": {
            const payload = parseAgentPayload(data);
            dispatch({
              type: BuilderActions.AGENT_TOOL_CALL,
              toolCall: {
                tool: payload.tool,
                input: payload.input,
                output: payload.output,
                timestamp: new Date().toISOString(),
              },
            });
            if (optimisticPlanRef.current?.kind === "approve") {
              clearOptimisticPlan();
            }
            break;
          }

          case "AGENT_MESSAGE": {
            const payload = parseAgentPayload(data);
            if (payload.content) {
              // Check if this is planning progress (shown in loading indicator, not as chat message)
              if (data.planningProgress) {
                dispatch({
                  type: BuilderActions.AGENT_PLANNING_PROGRESS,
                  content: payload.content,
                });
              }
            }
            break;
          }

          case "AGENT_TOKEN_USAGE": {
            const payload = parseAgentPayload(data);
            dispatch({
              type: BuilderActions.AGENT_TOKEN_USAGE,
              inputTokens: payload.input || 0,
              outputTokens: payload.output || 0,
              totalCost: payload.costUsd || 0,
            });
            if (optimisticPlanRef.current?.kind === "approve") {
              clearOptimisticPlan();
            }
            break;
          }

          case "AGENT_COMPLETE": {
            const payload = parseAgentPayload(data);
            dispatch({
              type: BuilderActions.AGENT_COMPLETE,
              success: payload.success,
              summary: payload.summary,
              totalCost: payload.totalCostUsd,
            });
            if (optimisticPlanRef.current?.kind === "approve") {
              clearOptimisticPlan();
            }
            break;
          }

          case "AGENT_ERROR": {
            const payload = parseAgentPayload(data);
            dispatch({
              type: BuilderActions.AGENT_ERROR,
              message: payload.message,
              recoverable: payload.recoverable,
            });
            break;
          }

          case "ERROR":
            rollbackOptimisticPlan();
            dispatch({
              type: BuilderActions.MESSAGE_ERROR,
              error: { message: data.message || label(intl, "builder_server_error") },
            });
            break;

          case "PROJECT_EVENT": {
            // Real-time project event update (file change rule triggered)
            const payload = parseAgentPayload(data);
            if (payload.id && project?.uid) {
              dispatch(receiveProjectEvent(project.uid, payload));
            }
            break;
          }

          // Note: ASK_USER_QUESTION is now saved in the database by the backend
          // and sent as POST_CHAT_MESSAGE_RESPONSE, which is handled above.
          // The message contains metadataJson with questionType: "askUserQuestion".

          default:
            console.log("[Builder WS] Unknown message type:", data.type);
        }
      } catch (err) {
        console.error("[Builder WS] Failed to parse message:", err);
      }
    };

    ws.onerror = (error) => {
      console.error("[Builder WS] Error:", error);
      rollbackOptimisticPlan();
      dispatch({
        type: BuilderActions.MESSAGE_ERROR,
        error: { message: "WebSocket error occurred" },
      });
    };
  }, [ws, wsReady, dispatch, rollbackOptimisticPlan, clearOptimisticPlan, project]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, replyInProgress]);

  // Approve plan function
  const approvePlan = useCallback(() => {
    if (!ws || !wsReady) return;

    if (pendingPlanIndex >= 0) {
      const pendingPlan = messages[pendingPlanIndex];
      optimisticPlanRef.current = {
        kind: "approve",
        messageUid: pendingPlan.uid,
        previousMessage: { ...pendingPlan },
      };
      const updatedPlan = updatePlanMetadata(pendingPlan, "approved");
      dispatch({
        type: BuilderActions.UPDATE_CHAT_MESSAGE,
        message: updatedPlan,
      });
    }

    dispatch({ type: BuilderActions.APPROVING_PLAN });

    ws.send(JSON.stringify({
      type: "APPROVE_PLAN",
      workspaceUid: workspaceId,
      chatUid: chatId,
    }));
  }, [ws, wsReady, dispatch, workspaceId, chatId, pendingPlanIndex, messages]);

  // Refine plan function
  const refinePlan = useCallback((feedback) => {
    if (!ws || !wsReady || !feedback) return;

    if (pendingPlanIndex >= 0) {
      const pendingPlan = messages[pendingPlanIndex];
      optimisticPlanRef.current = {
        kind: "refine",
        messageUid: pendingPlan.uid,
        previousMessage: { ...pendingPlan },
      };
      const updatedPlan = updatePlanMetadata(pendingPlan, "refined");
      dispatch({
        type: BuilderActions.UPDATE_CHAT_MESSAGE,
        message: updatedPlan,
      });
    }

    dispatch({
      type: BuilderActions.POSTED_MESSAGE,
      content: feedback,
    });

    ws.send(JSON.stringify({
      type: "REFINE_PLAN",
      workspaceUid: workspaceId,
      chatUid: chatId,
      content: feedback,
    }));
  }, [ws, wsReady, dispatch, workspaceId, chatId, pendingPlanIndex, messages]);

  // Stop agent function
  const stopAgent = useCallback(() => {
    if (!ws || !wsReady || !agentSession?.sessionId) return;

    ws.send(JSON.stringify({
      type: "AGENT_STOP",
      workspaceUid: workspaceId,
      chatUid: chatId,
      agentSessionUid: agentSession.sessionId,
    }));
  }, [ws, wsReady, workspaceId, chatId, agentSession?.sessionId]);

  // Submit answers to AskUserQuestion
  const submitQuestionAnswers = useCallback((toolUseId, answers) => {
    if (!ws || !wsReady) return;

    ws.send(JSON.stringify({
      type: "ASK_USER_RESPONSE",
      workspaceUid: workspaceId,
      chatUid: chatId,
      toolUseId,
      answers,
    }));
  }, [ws, wsReady, workspaceId, chatId]);

  // Send user input to running agent
  const sendAgentInput = useCallback((message) => {
    if (!ws || !wsReady || !isAgentRunning || !agentSession?.sessionId) return;

    // Add user message to the chat
    dispatch({
      type: BuilderActions.POSTED_MESSAGE,
      content: message,
      skipReplyInProgress: true,
    });

    // Send to agent via WebSocket
    ws.send(JSON.stringify({
      type: "AGENT_USER_INPUT",
      workspaceUid: workspaceId,
      chatUid: chatId,
      agentSessionUid: agentSession.sessionId,
      content: message,
    }));
  }, [ws, wsReady, isAgentRunning, dispatch, workspaceId, chatId, agentSession?.sessionId]);

  const handleSend = useCallback((content) => {
    const trimmed = content.trim();
    if (!trimmed) return;
    const isPlanningPhase = isAgentRunning && !hasApprovedPlan;
    if (hasPendingPlan || isPlanningPhase) {
      refinePlan(trimmed);
      return;
    }
    if (isAgentRunning) {
      sendAgentInput(trimmed);
      return;
    }
    sendChatMessage(trimmed);
  }, [hasPendingPlan, isAgentRunning, hasApprovedPlan, refinePlan, sendAgentInput, sendChatMessage]);

  // Project name editing handlers
  const startEditingName = useCallback(() => {
    setNameValue(project?.name || "");
    setEditingName(true);
  }, [project?.name]);

  const saveName = useCallback(async (e) => {
    e.preventDefault();

    // HTML5 validation - check and show native validation UI
    const valid = nameInputElem?.checkValidity();
    if (nameFormElem) nameFormElem.reportValidity();
    if (!valid) return;

    setSavingName(true);
    try {
      const updatedProject = await backend.updateBuilderProject(
        workspaceId,
        project.uid,
        { name: nameValue.trim() }
      );
      dispatch(updateCurrentProject(updatedProject));
      setEditingName(false);
      setSavedRecently(true);
      setTimeout(() => setSavedRecently(false), 5000);
    } catch (err) {
      console.error("Failed to save project name:", err);
    } finally {
      setSavingName(false);
    }
  }, [nameInputElem, nameFormElem, workspaceId, project?.uid, nameValue, dispatch]);

  const handleNameBlur = useCallback((e) => {
    // Don't trigger save if clicking the save button
    if (e.relatedTarget?.type === "submit") return;
    // Check if valid before saving on blur
    if (nameInputElem?.checkValidity()) {
      saveName(e);
    } else {
      // Invalid - just close without saving (user can try again)
      setEditingName(false);
    }
  }, [nameInputElem, saveName]);

  // Export as template handler
  const handleExportTemplate = useCallback(async () => {
    if (!project?.uid || templateExporting) return;

    try {
      const result = await dispatch(exportAsTemplate(intl, workspaceId, project.uid));
      if (result?.uid) {
        // Check if deployment succeeded
        if (result.deploymentStatus === "RUNNING") {
          toast.success(
            <div>
              <span>{label(intl, "template_created", "Template created successfully")}</span>
              <button
                className="toast_link_btn"
                onClick={() => nav(linkToTemplateDetail(workspaceId, result.uid))}
              >
                {label(intl, "view_template", "View template")}
              </button>
            </div>,
            { autoClose: 5000 }
          );
        } else if (result.deploymentStatus === "TIMEOUT") {
          toast.warning(
            label(intl, "template_deployment_timeout", "Template deployment is taking longer than expected. Check back later."),
            { autoClose: 5000 }
          );
        }
        // FAILED case is handled in action creator with Notify.error
      }
    } catch (err) {
      // Error already handled in action creator
    }
  }, [dispatch, intl, workspaceId, project?.uid, templateExporting, nav]);

  // Auto-resume when WebSocket connects and we have a loaded (not fresh) agent session
  // This prevents duplicate events when starting a new session (events stream live)
  // Only triggers on page reload when session was loaded from backend
  useEffect(() => {
    if (ws && wsReady && agentSession?.sessionId && shouldResumeAgent) {
      // Resume agent session - will replay missed events since lastEventId
      ws.send(JSON.stringify({
        type: "AGENT_RESUME",
        workspaceUid: workspaceId,
        chatUid: chatId,
        agentSessionUid: agentSession.sessionId,
        lastEventId: lastEventId || 0,
      }));
    }
  }, [ws, wsReady, workspaceId, chatId, agentSession?.sessionId, shouldResumeAgent, lastEventId]);

  // Determine if input should be disabled
  const isInputDisabled = !wsReady || replyInProgress;

  return (
    <NewWorkspaceFullpageLayout contentPanelClassName="project_chat_wrapper">
      <div className="project_chat_page">
        {/* Mobile tab navigation */}
        <div className="mobile_view_tabs">
          <button
            type="button"
            className={`mobile_tab ${mobileView === MOBILE_VIEWS.CHAT ? "active" : ""}`}
            onClick={() => setMobileView(MOBILE_VIEWS.CHAT)}
          >
            <Message id="builder_chat_tab" defaultMessage="Chat" />
          </button>
          <button
            type="button"
            className={`mobile_tab ${mobileView === MOBILE_VIEWS.PREVIEW ? "active" : ""}`}
            onClick={() => setMobileView(MOBILE_VIEWS.PREVIEW)}
          >
            <Message id="builder_preview_tab" defaultMessage="Preview" />
          </button>
        </div>

        {/* Chat column */}
        <div className={`chat_column ${mobileView === MOBILE_VIEWS.CHAT ? "active" : ""}`}>
          <div className="chat_header">
            <a href="/app" className="logo">
              <img
                src="/images/promptleo-logo-grey.svg"
                height="28"
                alt="Promptleo"
              />
            </a>
            <div className="breadcrumbs">
              <Link to={linkToBuilder(workspaceId)}>
                <Message id="app_builder_projects_title" />
              </Link>
              <span className="separator">/</span>
              {project ? (
                <form
                  className="project_name_form"
                  onSubmit={saveName}
                  ref={(val) => setNameFormElem(val)}
                >
                  {!editingName ? (
                    <span
                      className="project_name_display"
                      onClick={startEditingName}
                    >
                      {project.name}
                      {savedRecently ? (
                        <CheckIcon size="14" color="#28a745" />
                      ) : (
                        <Edit2Icon size="14" color="#7b809a" />
                      )}
                    </span>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="project_name_input"
                        value={nameValue}
                        onChange={(e) => setNameValue(e.target.value)}
                        onBlur={handleNameBlur}
                        autoFocus
                        required
                        ref={(val) => setNameInputElem(val)}
                      />
                      <button type="submit" className="save_name_btn" disabled={savingName}>
                        {savingName ? (
                          <Spinner />
                        ) : (
                          <CheckIcon size="14" color="#28a745" />
                        )}
                      </button>
                    </>
                  )}
                </form>
              ) : null}
            </div>
            <div className="header_actions">
              <button
                type="button"
                className="export_template_btn"
                onClick={handleExportTemplate}
                disabled={project?.deploymentStatus !== "RUNNING" || templateExporting}
                title={
                  project?.deploymentStatus !== "RUNNING"
                    ? label(intl, "deploy_first_to_export", "Deploy project first to export as template")
                    : label(intl, "export_as_template", "Export as template")
                }
              >
                {templateExporting ? <Spinner /> : <CloudUpload size={20} />}
              </button>
            </div>
          </div>

          <div className="chat_container">
            <ConnectionStatus
              status={connectionStatus}
              reconnectAttempts={reconnectAttempts}
              maxAttempts={maxReconnectAttempts}
              onRetry={retryConnection}
            />
            <div className="messages_feed">
              {!chat && (
                <div className="loading_state">
                  <Spinner />
                  <span><Message id="builder_loading_chat" /></span>
                </div>
              )}

              {chat && messages.length === 0 && !replyInProgress && (
                <div className="empty_chat">
                  <Message
                    id="start_conversation"
                    defaultMessage="Start a conversation..."
                  />
                </div>
              )}

              {messages.map((message, index) => (
                <BuilderMessage
                  key={message.uid || `msg-${index}`}
                  message={message}
                  onApprovePlan={approvePlan}
                  onRefinePlan={refinePlan}
                  onSubmitQuestionAnswers={submitQuestionAnswers}
                  showPlanActions={index === pendingPlanIndex}
                  isApproving={approvingPlan}
                />
              ))}

              {/* Loading state - show during initial request or planning phase */}
              {(replyInProgress || planningProgress) && (
                <BuilderReplyLoadingState planningProgress={planningProgress} />
              )}

              {/* Agent execution panel - show only after plan approval */}
              {agentSession && ['RUNNING', 'STOPPED', 'COMPLETED', 'FAILED'].includes(agentSession.status)
                && !planningProgress && hasApprovedPlan && !hasPendingPlan && (
                <AgentExecutionPanel />
              )}

              {replyError && (
                <div className="error_message">
                  {replyError.message || <Message id="builder_connection_error" />}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <BuilderInputArea
              onSend={handleSend}
              disabled={isInputDisabled}
              isLoading={replyInProgress}
              showStop={isAgentRunning}
              stopEnabled={wsReady && isAgentRunning}
              onStop={stopAgent}
            />
          </div>
        </div>

        {/* Preview column */}
        <div className={`preview_column ${mobileView === MOBILE_VIEWS.PREVIEW ? "active" : ""}`}>
          <PreviewPanel
            project={project}
            agentSession={agentSession}
            isAgentRunning={isAgentRunning}
          />
        </div>
      </div>
    </NewWorkspaceFullpageLayout>
  );
};

export default ProjectChatPage;
